import React, { useState, useEffect } from 'react';
import { Download, ArrowRight, AlertCircle, RefreshCw, FileText } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../../config';

const Reports = () => {
  const [periodMode, setPeriodMode] = useState('thisMonth');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  // Handle pre-calculated ranges
  useEffect(() => {
    const today = new Date();
    if (periodMode === 'thisMonth') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setFromDate(firstDay.toISOString().split('T')[0]);
      setToDate(today.toISOString().split('T')[0]);
    } else if (periodMode === 'last3Months') {
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setDate(today.getDate() - 90);
      setFromDate(threeMonthsAgo.toISOString().split('T')[0]);
      setToDate(today.toISOString().split('T')[0]);
    }
  }, [periodMode]);

  const fetchPreview = async () => {
    if (!fromDate || !toDate) {
      setError('Please select both from and to dates.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const accessTokenObj = JSON.parse(localStorage.getItem('accessToken') || '{}');
      const token = accessTokenObj.token || '';
      
      const res = await axios.get(`${API_BASE_URL}/api/reports/statement`, {
        params: { from: fromDate, to: toDate },
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatement(res.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to fetch statement preview.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!fromDate || !toDate) return;
    setExporting(true);
    setError('');
    try {
      const accessTokenObj = JSON.parse(localStorage.getItem('accessToken') || '{}');
      const token = accessTokenObj.token || '';

      const res = await fetch(`${API_BASE_URL}/api/reports/statement/export?from=${fromDate}&to=${toDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to generate PDF statement.');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FitScan_Statement_${fromDate.replace(/-/g, '')}_${toDate.replace(/-/g, '')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError('Failed to download PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn text-gray-200">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2 text-white">
          <FileText className="text-[#6CD800]" size={24} />
          Reports & Statements
        </h1>
        <p className="text-gray-400 mt-1">Generate and export official business and revenue statements for your gym.</p>
      </div>

      {/* Date Selectors */}
      <div className="bg-[#181818] border border-[#2b2b2b] p-5 rounded-xl space-y-4">
        <div className="flex flex-wrap gap-3">
          {[
            { id: 'thisMonth', label: 'This Month' },
            { id: 'last3Months', label: 'Last 3 Months' },
            { id: 'custom', label: 'Custom Range' }
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setPeriodMode(opt.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                periodMode === opt.id
                  ? 'bg-[#6CD800] text-[#181f10]'
                  : 'bg-[#222] text-gray-300 hover:bg-[#2b2b2b]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-end gap-4">
          <div className="w-full sm:w-auto">
            <label className="block text-xs text-gray-400 mb-1">From Date</label>
            <input
              type="date"
              value={fromDate}
              disabled={periodMode !== 'custom'}
              onChange={e => setFromDate(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white disabled:opacity-50 focus:outline-none focus:border-[#6CD800]"
            />
          </div>
          <div className="w-full sm:w-auto hidden sm:flex items-center justify-center h-10 text-gray-500">
            <ArrowRight size={16} />
          </div>
          <div className="w-full sm:w-auto">
            <label className="block text-xs text-gray-400 mb-1">To Date</label>
            <input
              type="date"
              value={toDate}
              disabled={periodMode !== 'custom'}
              onChange={e => setToDate(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white disabled:opacity-50 focus:outline-none focus:border-[#6CD800]"
            />
          </div>
          <button
            onClick={fetchPreview}
            disabled={loading}
            className="w-full sm:w-auto px-6 py-2 bg-[#222] border border-[#2b2b2b] text-white rounded-lg hover:border-[#6CD800] font-semibold transition-all flex items-center justify-center gap-2"
          >
            {loading ? <RefreshCw size={16} className="animate-spin" /> : 'Preview Statement'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl flex items-center gap-2 text-sm">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Statement Preview */}
      {statement && (
        <div className="space-y-6">
          <div className="flex justify-between items-center bg-[#181818] border border-[#2b2b2b] p-4 rounded-xl">
            <div>
              <h3 className="font-bold text-lg text-white">{statement.gymName}</h3>
              <p className="text-xs text-gray-400">Statement from {statement.from.split('T')[0]} to {statement.to.split('T')[0]}</p>
            </div>
            <button
              onClick={handleDownloadPdf}
              disabled={exporting}
              className="px-4 py-2 bg-[#6CD800] text-[#181f10] hover:bg-[#5bbf00] font-bold rounded-lg transition-all flex items-center gap-2 text-sm"
            >
              {exporting ? <RefreshCw size={16} className="animate-spin" /> : <Download size={16} />}
              Export PDF
            </button>
          </div>

          {/* Metrics summary */}
          <div className="grid grid-cols-2 sm:grid-cols-7 gap-4">
            {[
              { label: 'Total Revenue', val: `₹${(statement.totalRevenue || 0).toLocaleString('en-IN')}`, color: 'text-emerald-400' },
              { label: 'Total Expenses', val: `₹${(statement.totalExpenses || 0).toLocaleString('en-IN')}`, color: 'text-red-400' },
              { label: 'Net Balance', val: `₹${(statement.netBalance || 0).toLocaleString('en-IN')}`, color: (statement.netBalance || 0) >= 0 ? 'text-[#6CD800]' : 'text-red-450' },
              { label: 'Transactions', val: statement.totalTransactions, color: 'text-white' },
              { label: 'New Members', val: statement.newMembers, color: 'text-[#6CD800]' },
              { label: 'Renewals', val: statement.renewals, color: 'text-amber-400' },
              { label: 'Expired Count', val: statement.expiredCount, color: 'text-red-400' }
            ].map((card, i) => (
              <div key={i} className="bg-[#181818] border border-[#2b2b2b] p-4 rounded-xl">
                <span className="text-xs text-gray-400 block uppercase font-bold tracking-wider">{card.label}</span>
                <span className={`text-2xl font-bold mt-1 block ${card.color}`}>{card.val}</span>
              </div>
            ))}
          </div>

          {/* Plan Breakdown */}
          <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-5 space-y-4">
            <h4 className="font-bold text-white">Plan-wise Revenue</h4>
            <div className="space-y-3">
              {statement.planBreakdown.map((pb, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-white">{pb.planName} ({pb.transactionCount} transactions)</span>
                    <span className="font-bold text-[#6CD800]">₹{pb.totalRevenue.toLocaleString()} ({pb.percentage.toFixed(1)}%)</span>
                  </div>
                  <div className="h-2 bg-[#222] rounded-full overflow-hidden">
                    <div className="h-full bg-[#6CD800]" style={{ width: `${pb.percentage}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payments list preview */}
          <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl overflow-hidden">
            <div className="p-4 border-b border-[#2b2b2b] bg-[#222] flex justify-between items-center">
              <h4 className="font-bold text-white">Recent Payments</h4>
              {statement.payments.length > 10 && (
                <span className="text-xs text-gray-400">Showing 10 of {statement.payments.length} payments — full list included in PDF</span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-[#2b2b2b] text-gray-400 bg-[#1e1e1e]">
                    <th className="p-3">Date</th>
                    <th className="p-3">Member</th>
                    <th className="p-3">Plan</th>
                    <th className="p-3">Method</th>
                    <th className="p-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.payments.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="p-4 text-center text-gray-500">No payments found in this range.</td>
                    </tr>
                  ) : (
                    statement.payments.slice(0, 10).map((payment, i) => (
                      <tr key={i} className="border-b border-[#2b2b2b] hover:bg-[#222] transition-colors">
                        <td className="p-3 text-gray-400">{payment.paidDate.split('T')[0]}</td>
                        <td className="p-3 font-semibold text-white">{payment.memberName}</td>
                        <td className="p-3"><span className="bg-[#222] border border-[#333] px-2 py-0.5 rounded text-xs text-[#6CD800]">{payment.planName}</span></td>
                        <td className="p-3 text-gray-400">{payment.paymentMethod}</td>
                        <td className="p-3 text-right font-bold text-white">₹{payment.amount.toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Expenses list preview */}
          <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl overflow-hidden">
            <div className="p-4 border-b border-[#2b2b2b] bg-[#222] flex justify-between items-center">
              <h4 className="font-bold text-white">Recent Expenses</h4>
              {statement.expenses && statement.expenses.length > 10 && (
                <span className="text-xs text-gray-400">Showing 10 of {statement.expenses.length} expenses — full list included in PDF</span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-[#2b2b2b] text-gray-400 bg-[#1e1e1e]">
                    <th className="p-3">Date</th>
                    <th className="p-3">Category</th>
                    <th className="p-3">Note</th>
                    <th className="p-3">Created By</th>
                    <th className="p-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {!statement.expenses || statement.expenses.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="p-4 text-center text-gray-500">No expenses found in this range.</td>
                    </tr>
                  ) : (
                    statement.expenses.slice(0, 10).map((expense, i) => (
                      <tr key={i} className="border-b border-[#2b2b2b] hover:bg-[#222] transition-colors">
                        <td className="p-3 text-gray-400">{expense.date.split('T')[0]}</td>
                        <td className="p-3"><span className="bg-[#222] border border-[#333] px-2 py-0.5 rounded text-xs text-white">{expense.category}</span></td>
                        <td className="p-3 text-gray-400 truncate max-w-[200px]" title={expense.note}>{expense.note || '-'}</td>
                        <td className="p-3 text-gray-400">{expense.createdByUsername} ({expense.createdByRole})</td>
                        <td className="p-3 text-right font-bold text-white">₹{expense.amount.toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
